<template>
  <Teleport to="body">
    <transition name="context-menu-fade">
      <div
        v-if="visible"
        class="tab-context-menu"
        :style="{ left: x + 'px', top: y + 'px' }"
        @click="handleMenuClick"
      >
        <div class="menu-item" @click="handleRefresh">
          <el-icon><Refresh /></el-icon>
          <span>刷新页面</span>
        </div>
        <div v-if="tab?.closable" class="menu-item" @click="handleClose">
          <el-icon><Close /></el-icon>
          <span>关闭当前</span>
        </div>
        <el-divider class="menu-divider" />
        <div class="menu-item" @click="handleCloseOthers">
          <el-icon><CircleClose /></el-icon>
          <span>关闭其他</span>
        </div>
        <div class="menu-item" @click="handleCloseLeft">
          <el-icon><Back /></el-icon>
          <span>关闭左侧</span>
        </div>
        <div class="menu-item" @click="handleCloseRight">
          <el-icon><Right /></el-icon>
          <span>关闭右侧</span>
        </div>
        <el-divider class="menu-divider" />
        <div class="menu-item" @click="handleCloseAll">
          <el-icon><CloseBold /></el-icon>
          <span>关闭所有</span>
        </div>
      </div>
    </transition>

    <!-- 点击外部关闭菜单的遮罩 -->
    <div
      v-if="visible"
      class="context-menu-mask"
      @click="emit('close')"
      @contextmenu.prevent="emit('close')"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { Refresh, Close, CircleClose, Back, Right, CloseBold } from '@element-plus/icons-vue'
import { useTabsStore } from '@/stores/tabs'
import type { TabItem } from '@/stores/tabs'

interface Props {
  visible: boolean
  x: number
  y: number
  tab: TabItem | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const tabsStore = useTabsStore()

/**
 * 刷新页面
 */
const handleRefresh = () => {
  if (!props.tab) return

  const componentName = tabsStore.refreshTab(props.tab.path)
  if (componentName) {
    // 通过移除并重新添加到 KeepAlive 来实现刷新
    const currentRoute = router.currentRoute.value

    // 先从缓存中移除
    nextTick(() => {
      // 重新加载页面
      router.replace({
        path: '/redirect' + props.tab!.fullPath,
      })
    })
  }
  emit('close')
}

/**
 * 关闭当前
 */
const handleClose = () => {
  if (!props.tab) return

  const targetPath = tabsStore.removeTab(props.tab.path)
  if (targetPath) {
    router.push(targetPath)
  }
  emit('close')
}

/**
 * 关闭其他
 */
const handleCloseOthers = () => {
  if (!props.tab) return

  tabsStore.removeOtherTabs(props.tab.path)
  router.push(props.tab.fullPath)
  emit('close')
}

/**
 * 关闭左侧
 */
const handleCloseLeft = () => {
  if (!props.tab) return

  tabsStore.removeLeftTabs(props.tab.path)
  emit('close')
}

/**
 * 关闭右侧
 */
const handleCloseRight = () => {
  if (!props.tab) return

  tabsStore.removeRightTabs(props.tab.path)
  emit('close')
}

/**
 * 关闭所有
 */
const handleCloseAll = () => {
  const targetPath = tabsStore.removeAllTabs()
  router.push(targetPath)
  emit('close')
}

/**
 * 阻止菜单内部点击冒泡
 */
const handleMenuClick = (e: MouseEvent) => {
  e.stopPropagation()
}

/**
 * 监听 ESC 键关闭菜单
 */
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style lang="scss" scoped>
.context-menu-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1999;
}

.tab-context-menu {
  position: fixed;
  z-index: 2000;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  min-width: 140px;

  .menu-item {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    cursor: pointer;
    transition: all 0.2s;
    color: #606266;
    font-size: 13px;

    &:hover {
      background-color: #f5f7fa;
      color: #409eff;
    }

    .el-icon {
      margin-right: 8px;
      font-size: 14px;
    }
  }

  .menu-divider {
    margin: 4px 0;
  }
}

// 动画
.context-menu-fade-enter-active,
.context-menu-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.context-menu-fade-enter-from,
.context-menu-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
